import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PipelinePage from './pages/PipelinePage';
import JobDetailPage from './pages/JobDetailPage';
import LeadsPage from './pages/LeadsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import CommissionsPage from './pages/CommissionsPage';
import TeamPage from './pages/TeamPage';
import FinancialsPage from './pages/FinancialsPage';
import EstimatesPage from './pages/EstimatesPage';
import CalendarPage from './pages/CalendarPage';
import PrivacyPage from './pages/PrivacyPage';
import SmsConsentPreviewPage from './pages/SmsConsentPreviewPage';
import TermsPage from './pages/TermsPage';
import OnboardingPortal from './pages/OnboardingPortal';
import HandbookPage from './pages/HandbookPage';

import SalesManagerDashboard from './pages/SalesManagerDashboard';
import TrainingTrackerPage from './pages/TrainingTrackerPage';
import TeamActivityPage from './pages/TeamActivityPage';
import HiringPipelinePage from './pages/HiringPipelinePage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import SignPage from './pages/SignPage';
import ContactsPage from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import TasksPage from './pages/TasksPage';
import ContractSignPage from './pages/ContractSignPage';
import TvLeaderboardPage from './pages/TvLeaderboardPage';
import TvCalendarPage from './pages/TvCalendarPage';
import {
  GovDashboardPage,
  GovSolicitationsPage,
  GovSolicitationDetailPage,
  GovConstructionJobsPage,
  GovConstructionJobDetailPage,
  GovTasksPage,
  GovContactsPage,
  GovDocumentsPage,
  GovFinancialsPage,
} from './pages/gov';
import CompanyDocsPage from './features/company-docs/CompanyDocsPage';
import './App.css';
import { canUseWeb, isMobileRole, canAccessGovernment, isGovernmentOnlyRole } from './domain/access';
import IntegrationSettings from './features/integrations/IntegrationSettings';
import { environment } from './config';

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, logout } = useAuth();
  if (loading) return <div style={{ color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0F1923' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!canUseWeb(user.role)) {
    const handleLogout = () => {
      logout();
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0F1923', color: '#f1f5f9', gap: 16 }}>
        <div style={{ fontSize: 48 }}>📱</div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{isMobileRole(user.role) ? 'Use the Mobile App' : 'Workspace unavailable'}</div>
        <div style={{ color: '#94a3b8', fontSize: 14 }}>{isMobileRole(user.role) ? 'Your role uses the McMullen Browne mobile app.' : 'Your account does not have a supported web workspace. Contact your administrator.'}</div>
        <button
          onClick={handleLogout}
          style={{
            marginTop: 24,
            padding: '10px 24px',
            background: 'transparent',
            border: '1px solid #334155',
            borderRadius: 8,
            color: '#94a3b8',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Sign out
        </button>
      </div>
    );
  }
  // Cross-tab events may jump directly between accounts. Remount their page
  // state and discard old component callbacks, retaining same-user refreshes.
  return <React.Fragment key={user.id}>{children}</React.Fragment>;
};

const GovernmentRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return user && canAccessGovernment(user.role) ? <>{children}</> : <Navigate to="/" replace />;
};

const WorkspaceBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const contractingOfficerPath = location.pathname.startsWith('/gov') || location.pathname === '/company-docs';
  if (user && isGovernmentOnlyRole(user.role) && !contractingOfficerPath) {
    return <Navigate to="/gov/solicitations" replace />;
  }
  return <>{children}</>;
};

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/sms-consent" element={<SmsConsentPreviewPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/sign-contract/:token" element={<ContractSignPage />} />
      <Route path="/tv/leaderboard" element={<TvLeaderboardPage />} />
      <Route path="/tv/calendar" element={<TvCalendarPage />} />
      <Route path="/onboard/:token" element={<OnboardingPortal />} />
      <Route path="/handbook" element={<HandbookPage standalone />} />
      <Route path="/" element={<ProtectedRoute><WorkspaceBoundary><Layout /></WorkspaceBoundary></ProtectedRoute>}>
        <Route index element={user?.role === 'specialist' ? <Navigate to="/sales-manager" /> : <DashboardPage />} />
        <Route path="pipeline" element={<PipelinePage />} />
        <Route path="pipeline/:id" element={<JobDetailPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="contacts/:id" element={<ContactDetailPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="commissions" element={<CommissionsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="financials" element={<FinancialsPage />} />
        <Route path="estimates" element={<EstimatesPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="company-docs" element={<CompanyDocsPage />} />
        <Route path="settings" element={<IntegrationSettings />} />
        <Route path="calendar" element={<CalendarPage />} />

        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/:id" element={<EmployeeDetailPage />} />
        <Route path="sales-manager" element={<SalesManagerDashboard />} />
        <Route path="training-tracker" element={<TrainingTrackerPage />} />
        <Route path="team-activity" element={<TeamActivityPage />} />
        <Route path="hiring-pipeline" element={<HiringPipelinePage />} />
        {/* Government Contracting V3 — manual-entry CRM with 3 pipelines */}
        <Route path="gov" element={<GovernmentRoute><GovDashboardPage /></GovernmentRoute>} />
        <Route path="gov/solicitations" element={<GovernmentRoute><GovSolicitationsPage /></GovernmentRoute>} />
        <Route path="gov/solicitations/:id" element={<GovernmentRoute><GovSolicitationDetailPage /></GovernmentRoute>} />
        <Route path="gov/construction" element={<GovernmentRoute><GovConstructionJobsPage /></GovernmentRoute>} />
        <Route path="gov/construction/:id" element={<GovernmentRoute><GovConstructionJobDetailPage /></GovernmentRoute>} />
        <Route path="gov/tasks" element={<GovernmentRoute><GovTasksPage /></GovernmentRoute>} />
        <Route path="gov/contacts" element={<GovernmentRoute><GovContactsPage /></GovernmentRoute>} />
        <Route path="gov/documents" element={<GovernmentRoute><GovDocumentsPage /></GovernmentRoute>} />
        <Route path="gov/financials" element={<GovernmentRoute><GovFinancialsPage /></GovernmentRoute>} />
        <Route path="gov/calendar" element={<GovernmentRoute><CalendarPage /></GovernmentRoute>} />

        {/* Legacy redirects (V1/V2 routes → V3) */}
        <Route path="gov/opportunities" element={<Navigate to="/gov/solicitations" replace />} />
        <Route path="gov/opportunities/:id" element={<Navigate to="/gov/solicitations" replace />} />
        <Route path="gov/jobs" element={<Navigate to="/gov/construction" replace />} />
        <Route path="gov/jobs/:id" element={<Navigate to="/gov/construction" replace />} />
        <Route path="gov/pursuit" element={<Navigate to="/gov" replace />} />
        <Route path="gov/bids" element={<Navigate to="/gov" replace />} />
        <Route path="gov/bids/:id/build" element={<Navigate to="/gov" replace />} />
        <Route path="gov/scan/history" element={<Navigate to="/gov" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {environment.appEnv !== 'production' && <div role="status" style={{ position: 'fixed', bottom: 4, right: 4, zIndex: 10000, padding: '4px 8px', borderRadius: 4, background: '#F59E0B', color: '#111', fontSize: 11 }}>{environment.appEnv.toUpperCase()}</div>}
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
